import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { BookOpen, Headphones, Mic, PenTool, TrendingUp, Award, Clock, Users, Sparkles, Globe, ChevronRight, Calculator, Mail, MessageCircle, Menu, X, Facebook, Youtube, Instagram, Send, Star, Quote, CheckCircle, CheckSquare, Square } from 'lucide-react';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { settingsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Check } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [packs, setPacks] = useState([]);
  const [invitationCode, setInvitationCode] = useState(null);
  const [centerAdmin, setCenterAdmin] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  
  // NCLC Calculator state
  const [scores, setScores] = useState({
    compOrale: '',
    compEcrite: '',
    expOrale: '',
    expEcrite: ''
  });
  const [nclcLevels, setNclcLevels] = useState(null);
  const [selectedExam, setSelectedExam] = useState('TCF CANADA');

  useEffect(() => {
    const loadData = async () => {
      try {
        let finalPacks = [];
        // Check if there's an invitation code in the URL
        const code = searchParams.get('code');
        
        if (code) {
          setInvitationCode(code);
          // Store the center code for logout redirect
          localStorage.setItem('tcf_center_code', code.toUpperCase());
          
          // Find the admin by invitation code
          const adminResponse = await usersAPI.getAdminByCode(code);
          
          if (adminResponse?.found && adminResponse?.admin) {
            setCenterAdmin(adminResponse.admin);
            // Load center-specific settings ONLY (no SuperAdmin merge)
            const centerSettings = await settingsAPI.getCenterSettings(adminResponse.admin.id);
            
            // Use Admin's settings with neutral defaults (NOT SuperAdmin's branding)
            const adminSettings = {
              site_name: centerSettings?.site_name || centerSettings?.siteName || 'TCF Canada',
              logo: centerSettings?.logo || '',
              email: centerSettings?.email || '',
              phone: centerSettings?.phone || '',
              whatsapp: centerSettings?.whatsapp || '',
              address: centerSettings?.address || '',
              facebook: centerSettings?.facebook || '',
              youtube: centerSettings?.youtube || '',
              instagram: centerSettings?.instagram || '',
              telegram: centerSettings?.telegram || '',
              carousel_images: centerSettings?.carousel_images || null,
            };
            setSiteSettings(adminSettings);
            
            // Load center-specific packs and testimonials
            finalPacks = await settingsAPI.getCenterPacks(adminResponse.admin.id);
            try {
              const testis = await settingsAPI.getCenterTestimonials(adminResponse.admin.id);
              setTestimonials(Array.isArray(testis) ? testis.filter(t => t.is_active) : []);
            } catch { /* testimonials optional */ }
          } else {
            // Invalid code, load default settings and global packs
            const loadedSettings = await settingsAPI.getSiteSettings();
            setSiteSettings(loadedSettings || {});
            finalPacks = await settingsAPI.getPacks();
          }
        } else {
          // No invitation code, load default SuperAdmin settings and global packs
          const loadedSettings = await settingsAPI.getSiteSettings();
          setSiteSettings(loadedSettings || {});
          finalPacks = await settingsAPI.getPacks();
        }
        
        setPacks(Array.isArray(finalPacks) ? finalPacks : []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, [searchParams]);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const slides = siteSettings.carousel_images?.length > 0 
    ? siteSettings.carousel_images.map((img, i) => ({
        image: img.startsWith('http') ? img : `${API_URL}${img}`,
        alt: `Slide ${i + 1}`
      }))
    : [
        {
          image: 'https://images.unsplash.com/photo-1751738866641-4205ba6fb14e?q=85',
          alt: 'Montréal'
        },
        {
          image: 'https://images.unsplash.com/photo-1687763340395-7d074b55e698?q=85',
          alt: 'Québec City'
        },
        {
          image: 'https://images.unsplash.com/photo-1663602003573-d2a029baa5fc?q=85',
          alt: 'Toronto'
        }
      ];

  // Helper function to preserve invitation code in navigation
  const getLoginUrl = () => invitationCode ? `/login?code=${invitationCode}` : '/login';
  const navigateToLogin = () => navigate(getLoginUrl());

  const epreuves = [
    {
      id: 'comprehension-orale',
      title: 'Compréhension Orale',
      description: 'Écoutez des documents audio et répondez aux questions.',
      icon: Headphones,
      questions: 39,
      duree: '35 minutes',
      color: 'bg-green-500',
      path: '/modules/comprehension-orale'
    },
    {
      id: 'comprehension-ecrite',
      title: 'Compréhension Écrite',
      description: 'Lisez des textes et répondez aux questions.',
      icon: BookOpen,
      questions: 39,
      duree: '60 minutes',
      color: 'bg-blue-500',
      path: '/modules/comprehension-ecrite'
    },
    {
      id: 'expression-orale',
      title: 'Expression Orale',
      description: 'Exprimez-vous oralement sur différents sujets.',
      icon: Mic,
      questions: 3,
      duree: '12 minutes',
      color: 'bg-orange-500',
      path: '/modules/expression-orale'
    },
    {
      id: 'expression-ecrite',
      title: 'Expression Écrite',
      description: 'Rédigez des textes de différents types.',
      icon: PenTool,
      questions: 3,
      duree: '60 minutes',
      color: 'bg-purple-500',
      path: '/modules/expression-ecrite'
    }
  ];

  const avantages = [
    {
      icon: TrendingUp,
      title: 'Suivi de Progression',
      description: 'Suivez vos performances en temps réel et identifiez vos points à améliorer.'
    },
    {
      icon: Sparkles,
      title: 'Simulateur IA',
      description: "Notre simulateur d'expression écrite corrige vos textes avec l'intelligence artificielle."
    },
    {
      icon: Award,
      title: 'Version 2026',
      description: "Contenus conformes aux dernières mises à jour de l'examen TCF Canada."
    },
    {
      icon: Users,
      title: 'Accompagnement Personnalisé',
      description: 'Des formateurs expérimentés vous guident vers la réussite.'
    },
    {
      icon: Clock,
      title: 'Conditions Réelles',
      description: "Entraînez-vous dans les mêmes conditions que l'examen officiel."
    },
    {
      icon: Globe,
      title: 'Accès 24/7',
      description: 'Révisez à votre rythme, où que vous soyez, quand vous le souhaitez.'
    }
  ];

  const faq = [
    {
      question: 'Les tests sont-ils similaires à l\'examen réel ?',
      answer: 'Oui, nos tests sont conçus pour reproduire exactement les conditions de l\'examen TCF Canada officiel : même format, même durée, même niveau de difficulté.'
    },
    {
      question: 'Combien de temps pour se préparer au TCF Canada ?',
      answer: 'La durée de préparation dépend de votre niveau actuel. En moyenne, 1 à 2 mois de préparation intensive permettent d\'obtenir de bons résultats.'
    },
    {
      question: 'Le simulateur d\'expression écrite corrige-t-il vraiment bien ?',
      answer: 'Notre simulateur utilise l\'intelligence artificielle pour analyser votre texte et fournir des corrections détaillées sur la grammaire, le vocabulaire et la structure.'
    },
    {
      question: 'Puis-je accéder à la plateforme depuis mon téléphone ?',
      answer: 'Oui, notre plateforme est entièrement responsive et accessible depuis tous vos appareils : ordinateur, tablette et smartphone.'
    }
  ];

  const nclcTable = [
    { nclc: '10+', compEcrite: '549-699', expEcrite: '16-20', compOrale: '549-699', expOrale: '16-20' },
    { nclc: '9', compEcrite: '524-548', expEcrite: '14-15', compOrale: '523-548', expOrale: '14-15' },
    { nclc: '8', compEcrite: '499-523', expEcrite: '12-13', compOrale: '503-522', expOrale: '12-13' },
    { nclc: '7', compEcrite: '453-498', expEcrite: '10-11', compOrale: '458-502', expOrale: '10-11' },
    { nclc: '6', compEcrite: '406-452', expEcrite: '7-9', compOrale: '398-457', expOrale: '7-9' },
    { nclc: '5', compEcrite: '375-405', expEcrite: '6', compOrale: '369-397', expOrale: '6' },
    { nclc: '4', compEcrite: '342-374', expEcrite: '4-5', compOrale: '331-368', expOrale: '4-5' }
  ];

  const getNiveau = (score, type) => {
    const scoreNum = parseInt(score);
    if (!scoreNum || scoreNum <= 0) return '-';
    for (const row of nclcTable) {
      const range = row[type];
      if (range.includes('-')) {
        const [min, max] = range.split('-').map(Number);
        if (scoreNum >= min && scoreNum <= max) return row.nclc;
      } else {
        if (scoreNum === parseInt(range)) return row.nclc;
      }
    }
    return '-';
  };

  const getNclcNumeric = (lvl) => {
    if (!lvl || lvl === '-') return 0;
    return lvl === '10+' ? 10 : (parseInt(lvl) || 0);
  };

  const getNclcColor = (lvl) => {
    const n = getNclcNumeric(lvl);
    if (n >= 10) return 'bg-emerald-500 text-white';
    if (n >= 8) return 'bg-green-500 text-white';
    if (n >= 7) return 'bg-blue-500 text-white';
    if (n >= 6) return 'bg-amber-500 text-white';
    if (n >= 4) return 'bg-orange-500 text-white';
    return 'bg-muted text-muted-foreground';
  };

  const getNclcBgClass = (lvl) => {
    const n = getNclcNumeric(lvl);
    if (n >= 10) return 'bg-emerald-500';
    if (n >= 8) return 'bg-green-500';
    if (n >= 7) return 'bg-blue-500';
    if (n >= 6) return 'bg-amber-500';
    if (n >= 4) return 'bg-orange-500';
    return 'bg-secondary';
  };

  const getNclcLabel = (lvl) => {
    const n = getNclcNumeric(lvl);
    if (n >= 10) return 'Niveau C2 — Excellent';
    if (n >= 9) return 'Niveau C1 — Très avancé';
    if (n >= 8) return 'Niveau B2+ — Avancé';
    if (n >= 7) return 'Niveau B2 — Intermédiaire avancé';
    if (n >= 6) return 'Niveau B1+ — Intermédiaire';
    if (n >= 4) return 'Niveau A2+ — Débutant avancé';
    return '';
  };

  useEffect(() => {
    const hasAny = scores.compOrale || scores.compEcrite || scores.expOrale || scores.expEcrite;
    if (!hasAny) { setNclcLevels(null); return; }
    setNclcLevels({
      compOrale: getNiveau(scores.compOrale, 'compOrale'),
      compEcrite: getNiveau(scores.compEcrite, 'compEcrite'),
      expOrale: getNiveau(scores.expOrale, 'expOrale'),
      expEcrite: getNiveau(scores.expEcrite, 'expEcrite'),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const navLinks = [
    { label: 'Compréhension Orale', href: '#epreuves' },
    { label: 'Compréhension Écrite', href: '#epreuves' },
    { label: 'Expression Orale', href: '#epreuves' },
    { label: 'Expression Écrite', href: '#epreuves' },
    { label: 'Méthodologie', href: '/methodologie', isRoute: true },
    { label: 'Tarification', href: '/tarification', isRoute: true },
  ];

  const nclcModuleKeys = ['compOrale', 'compEcrite', 'expOrale', 'expEcrite'];
  const nclcNums = nclcLevels ? nclcModuleKeys.map(k => getNclcNumeric(nclcLevels[k])) : [];
  const validNums = nclcNums.filter(n => n > 0);
  const overallNclcNum = validNums.length ? Math.min(...validNums) : 0;
  const overallNclc = overallNclcNum === 10 ? '10+' : overallNclcNum > 0 ? String(overallNclcNum) : null;
  const entreeExpress = validNums.length === 4 && validNums.every(n => n >= 7);
  const arrima = entreeExpress;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo + Site Name */}
            <div className="flex items-center space-x-3">
              {siteSettings.logo ? (
                <img 
                  src={siteSettings.logo.startsWith('http') ? siteSettings.logo : `${process.env.REACT_APP_BACKEND_URL || ''}${siteSettings.logo}`} 
                  alt={siteSettings.site_name || 'Logo'} 
                  className="h-12 w-12 object-contain rounded-lg shadow-sm"
                />
              ) : (
                <div className="h-12 w-12 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center shadow-sm">
                  <span className="text-xl font-bold text-primary-foreground">
                    {(siteSettings.site_name || 'TCF').charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-primary leading-tight">
                  {siteSettings.site_name || 'TCF Canada'}
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">Préparation TCF</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link, index) => (
                link.isRoute ? (
                  <Link
                    key={index}
                    to={invitationCode ? `${link.href}?code=${invitationCode}` : link.href}
                    className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={index}
                    href={link.href}
                    className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                )
              ))}
            </div>

            <div className="flex items-center space-x-4">
              <DarkModeToggle />
              
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>

              {user ? (
                <div className="hidden lg:flex items-center space-x-3">
                  <Link
                    to="/mon-compte"
                    data-testid="nav-mon-compte-button"
                    className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors font-medium text-sm"
                  >
                    Mon Compte
                  </Link>
                  <Link
                    to="/dashboard"
                    data-testid="nav-dashboard-button"
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm"
                  >
                    Tableau de bord
                  </Link>
                </div>
              ) : (
                <Link
                  to={getLoginUrl()}
                  data-testid="nav-login-button"
                  className="hidden lg:block px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                >
                  Se connecter
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t animate-in slide-in-from-top-2">
              <div className="flex flex-col space-y-2">
                {navLinks.map((link, index) => (
                  link.isRoute ? (
                    <Link
                      key={index}
                      to={invitationCode ? `${link.href}?code=${invitationCode}` : link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 rounded-lg hover:bg-secondary transition-colors font-medium"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={index}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 rounded-lg hover:bg-secondary transition-colors font-medium"
                    >
                      {link.label}
                    </a>
                  )
                ))}
                <div className="border-t pt-2 mt-2">
                  {user ? (
                    <>
                      <Link
                        to="/mon-compte"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 rounded-lg hover:bg-secondary transition-colors font-medium"
                      >
                        Mon Compte
                      </Link>
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-center mt-2"
                      >
                        Tableau de bord
                      </Link>
                    </>
                  ) : (
                    <Link
                      to={getLoginUrl()}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-center"
                    >
                      Se connecter
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section with Carousel */}
      <section className="relative min-h-[400px] h-[600px] mt-16 overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40" />
          </div>
        ))}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl animate-in">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
              Se préparer au TCF Canada - TCF Québec
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-white/90">
              Plate-forme spécialisée dans la préparation au TCF Canada<br className="hidden sm:block" />
              {' '}Tests en conditions réelles.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={navigateToLogin}
                className="px-8 py-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold text-lg"
              >
                Commencer maintenant
              </button>
            </div>
          </div>
        </div>
        
        {/* Carousel indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Les 4 Épreuves */}
      <section id="epreuves" className="py-20 px-4 bg-secondary/30 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Les 4 Épreuves du TCF Canada</h2>
            <p className="text-lg text-muted-foreground">
              Maîtrisez chaque épreuve avec nos entraînements spécialisés et nos simulations en conditions réelles.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {epreuves.map((epreuve) => {
              const Icon = epreuve.icon;
              return (
                <div
                  key={epreuve.id}
                  className="bg-card p-8 rounded-xl border shadow-sm hover:shadow-lg transition-all group"
                  data-testid={`epreuve-${epreuve.id}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`${epreuve.color} p-4 rounded-xl text-white`}>
                      <Icon className="h-8 w-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-2">{epreuve.title}</h3>
                      <p className="text-muted-foreground mb-4">{epreuve.description}</p>
                      <div className="flex items-center space-x-6 text-sm mb-4">
                        <span className="font-medium">{epreuve.questions} questions</span>
                        <span className="font-medium">{epreuve.duree}</span>
                      </div>
                      <button
                        onClick={navigateToLogin}
                        className="flex items-center space-x-2 text-primary font-semibold group-hover:translate-x-2 transition-transform"
                      >
                        <span>Commencer</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nos Avantages */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Nos Avantages</h2>
            <p className="text-lg text-muted-foreground">
              Tout ce dont vous avez besoin pour réussir votre TCF Canada
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {avantages.map((avantage, index) => {
              const Icon = avantage.icon;
              return (
                <div
                  key={index}
                  className="bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
                >
                  <Icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{avantage.title}</h3>
                  <p className="text-muted-foreground">{avantage.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nos Tarifs */}
      <section id="tarifs" className="py-20 px-4 bg-secondary/30 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm text-primary font-semibold mb-2">Choisissez votre formule</p>
            <h2 className="text-4xl font-bold mb-4">Nos Tarifs</h2>
            <p className="text-lg text-muted-foreground">
              Des formules adaptées à vos besoins pour réussir votre TCF Canada
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packs.map((pack) => (
              <div
                key={pack.id}
                data-testid={`landing-pack-${pack.id}`}
                className={`relative bg-card p-8 rounded-2xl border-2 shadow-lg transition-all hover:shadow-xl ${
                  pack.popular
                    ? 'border-primary md:scale-105'
                    : 'border-transparent hover:border-primary/30'
                }`}
              >
                {pack.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-primary-foreground text-sm font-bold px-4 py-1 rounded-full">
                      POPULAIRE
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{pack.name}</h3>
                  <div className="flex items-baseline justify-center space-x-1">
                    <span className="text-4xl font-bold">${pack.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">Durée : {pack.duration}</p>
                </div>

                <div className="space-y-3 mb-8">
                  {pack.features.map((feature, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="h-5 w-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-green-600" />
                      </div>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg text-center mb-6">
                  <p className="font-semibold text-primary">
                    {pack.essais_ia || pack.essaisIA || 0} essais IA inclus
                  </p>
                </div>

                <button
                  onClick={() => {
                    const paymentLink = pack.payment_link || pack.paymentLink;
                    if (paymentLink) {
                      const url = paymentLink.startsWith('http://') || paymentLink.startsWith('https://') 
                        ? paymentLink 
                        : 'https://' + paymentLink;
                      window.open(url, '_blank');
                    } else {
                      navigateToLogin();
                    }
                  }}
                  data-testid={`landing-subscribe-${pack.id}`}
                  className="w-full py-4 rounded-xl font-semibold transition-all bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  S'abonner
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link 
              to={invitationCode ? `/tarification?code=${invitationCode}` : '/tarification'} 
              className="text-primary font-semibold hover:underline inline-flex items-center space-x-2"
            >
              <span>Voir tous les détails des tarifs</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Calculateur NCLC */}
      <section className="py-20 px-4 bg-secondary/20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-sm text-primary font-semibold mb-2">Outil gratuit</p>
            <h2 className="text-4xl font-bold mb-4">Calculez votre Niveau NCLC</h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Entrez vos scores pour connaître votre niveau et votre éligibilité à l'immigration canadienne
            </p>
          </div>

          {/* Exam selector */}
          <div className="flex justify-center mb-8 px-2">
            <div className="flex flex-wrap justify-center bg-card border rounded-xl p-1 gap-1 shadow-sm w-full max-w-sm sm:max-w-none sm:inline-flex">
              {['TCF CANADA', 'TEF CANADA', 'IELTS', 'CELPIP'].map(exam => (
                <button
                  key={exam}
                  type="button"
                  onClick={() => exam === 'TCF CANADA' && setSelectedExam(exam)}
                  className={`px-3 sm:px-5 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                    exam === selectedExam
                      ? 'bg-primary text-primary-foreground shadow'
                      : exam === 'TCF CANADA'
                        ? 'text-muted-foreground hover:text-foreground'
                        : 'text-muted-foreground/40 cursor-not-allowed'
                  }`}
                >
                  {exam}
                  {exam !== 'TCF CANADA' && (
                    <span className="ml-1 text-xs opacity-60">bientôt</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Two-panel calculator */}
          <div className="bg-card rounded-2xl border shadow-lg overflow-hidden mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x">

              {/* Left panel: inputs */}
              <div className="p-8">
                <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-primary" />
                  Vos scores TCF Canada
                </h3>
                <div className="space-y-5">
                  {[
                    { key: 'compOrale',  label: 'Compréhension Orale',  icon: Headphones, color: 'text-green-500',  bg: 'bg-green-500/10',  placeholder: '0 – 699', nclcKey: 'compOrale'  },
                    { key: 'compEcrite', label: 'Compréhension Écrite', icon: BookOpen,   color: 'text-blue-500',   bg: 'bg-blue-500/10',   placeholder: '0 – 699', nclcKey: 'compEcrite' },
                    { key: 'expOrale',   label: 'Expression Orale',     icon: Mic,        color: 'text-orange-500', bg: 'bg-orange-500/10', placeholder: '0 – 20',  nclcKey: 'expOrale'   },
                    { key: 'expEcrite',  label: 'Expression Écrite',    icon: PenTool,    color: 'text-purple-500', bg: 'bg-purple-500/10', placeholder: '0 – 20',  nclcKey: 'expEcrite'  },
                  ].map(({ key, label, icon: Icon, color, bg, placeholder, nclcKey }) => {
                    const lvl = nclcLevels?.[nclcKey];
                    const hasLevel = lvl && lvl !== '-';
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <span className={`p-1.5 rounded-lg ${bg}`}>
                              <Icon className={`h-4 w-4 ${color}`} />
                            </span>
                            {label}
                          </label>
                          {hasLevel && (
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${getNclcColor(lvl)}`}>
                              NCLC {lvl}
                            </span>
                          )}
                        </div>
                        <input
                          type="number"
                          value={scores[key]}
                          onChange={(e) => setScores(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className="w-full h-11 px-4 rounded-xl border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right panel: results */}
              <div className="p-8 bg-secondary/10">
                {nclcLevels ? (
                  <div className="space-y-6">
                    {/* Overall NCLC */}
                    <div>
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-3">Niveau global</p>
                      <div className="flex items-center gap-4">
                        <div className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-md shrink-0 ${overallNclc ? getNclcBgClass(overallNclc) : 'bg-muted'}`}>
                          <span className="text-3xl font-black text-white">{overallNclc || '–'}</span>
                        </div>
                        <div>
                          <p className="text-2xl font-bold leading-tight">{overallNclc ? `NCLC ${overallNclc}` : '–'}</p>
                          <p className="text-sm text-muted-foreground mt-0.5">{overallNclc ? getNclcLabel(overallNclc) : 'Scores incomplets'}</p>
                          <p className="text-xs text-muted-foreground mt-1">Basé sur la compétence la plus faible</p>
                        </div>
                      </div>
                    </div>

                    {/* Eligibility */}
                    <div>
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-3">Éligibilité immigration</p>
                      <div className="space-y-3">
                        <div className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all ${entreeExpress ? 'bg-green-500/10 border-green-500/30' : 'bg-secondary/50 border-border'}`}>
                          {entreeExpress
                            ? <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                            : <X className="h-5 w-5 text-muted-foreground shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold">Entrée Express</p>
                            <p className="text-xs text-muted-foreground">Tous les scores ≥ NCLC 7</p>
                          </div>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${entreeExpress ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                            {entreeExpress ? 'Éligible' : 'Non éligible'}
                          </span>
                        </div>
                        <div className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all ${arrima ? 'bg-blue-500/10 border-blue-500/30' : 'bg-secondary/50 border-border'}`}>
                          {arrima
                            ? <CheckCircle className="h-5 w-5 text-blue-500 shrink-0" />
                            : <X className="h-5 w-5 text-muted-foreground shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold">ARRIMA Québec</p>
                            <p className="text-xs text-muted-foreground">Tous les scores ≥ NCLC 7</p>
                          </div>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${arrima ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                            {arrima ? 'Éligible' : 'Non éligible'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Per-module breakdown */}
                    <div>
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-3">Détail par compétence</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'Comp. Orale',  key: 'compOrale',  color: 'text-green-500'  },
                          { label: 'Comp. Écrite', key: 'compEcrite', color: 'text-blue-500'   },
                          { label: 'Exp. Orale',   key: 'expOrale',   color: 'text-orange-500' },
                          { label: 'Exp. Écrite',  key: 'expEcrite',  color: 'text-purple-500' },
                        ].map(({ label, key, color }) => {
                          const lvl = nclcLevels[key];
                          return (
                            <div key={key} className="bg-card rounded-xl p-3 border text-center">
                              <p className="text-xs text-muted-foreground mb-1">{label}</p>
                              <p className={`text-lg font-bold ${lvl !== '-' ? color : 'text-muted-foreground'}`}>
                                {lvl !== '-' ? `NCLC ${lvl}` : '–'}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center py-16">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                      <Calculator className="h-8 w-8 text-primary" />
                    </div>
                    <p className="text-lg font-semibold mb-2">Entrez vos scores</p>
                    <p className="text-sm text-muted-foreground max-w-xs">
                      Remplissez les champs à gauche pour voir votre niveau NCLC et votre éligibilité à l'immigration
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tableau NCLC */}
          <div className="bg-card p-6 rounded-2xl border shadow-sm">
            <h3 className="text-xl font-semibold mb-1">Tableau d'équivalence NCLC officiel</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Référence officielle pour convertir vos scores TCF Canada en niveaux NCLC
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary rounded-xl">
                    <th className="px-4 py-3 text-left font-semibold rounded-l-xl">NCLC</th>
                    <th className="px-4 py-3 text-left font-semibold">Comp. Écrite</th>
                    <th className="px-4 py-3 text-left font-semibold">Exp. Écrite</th>
                    <th className="px-4 py-3 text-left font-semibold">Comp. Orale</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-r-xl">Exp. Orale</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {nclcTable.map((row, index) => {
                    const isActive = overallNclc === row.nclc;
                    return (
                      <tr key={index} className={isActive ? 'bg-primary/10 font-semibold' : ''}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getNclcColor(row.nclc)}`}>
                            {row.nclc}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{row.compEcrite}</td>
                        <td className="px-4 py-3 text-muted-foreground">{row.expEcrite}</td>
                        <td className="px-4 py-3 text-muted-foreground">{row.compOrale}</td>
                        <td className="px-4 py-3 text-muted-foreground">{row.expOrale}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Questions Fréquentes</h2>
            <p className="text-lg text-muted-foreground">
              Les réponses aux questions les plus posées
            </p>
          </div>
          <div className="space-y-4">
            {faq.map((item, index) => (
              <details
                key={index}
                className="bg-card p-6 rounded-xl border group"
              >
                <summary className="font-semibold cursor-pointer flex items-center justify-between">
                  {item.question}
                  <ChevronRight className="h-5 w-5 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-muted-foreground">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-primary font-semibold text-sm uppercase tracking-widest mb-3">Témoignages</span>
              <h2 className="text-4xl font-bold mb-4">Ce que disent nos étudiants</h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                Des étudiants qui ont réussi leur TCF Canada avec notre plateforme
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => {
                const imgSrc = t.image_url
                  ? (t.image_url.startsWith('http') ? t.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${t.image_url}`)
                  : null;
                return (
                  <div key={t.id} className="bg-card rounded-2xl border p-6 flex flex-col shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5">
                    {/* Stars */}
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} className={`h-4 w-4 ${n <= (t.rating || 5) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
                      ))}
                    </div>

                    {/* Quote icon + text */}
                    <Quote className="h-7 w-7 text-primary/25 mb-2 flex-shrink-0" />
                    <p className="text-muted-foreground text-sm leading-relaxed flex-1 mb-5">
                      {t.content}
                    </p>

                    {/* Score badge */}
                    {t.score && (
                      <div className="mb-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full">
                          <Award className="h-3 w-3" />
                          {t.score}
                        </span>
                      </div>
                    )}

                    {/* Author */}
                    <div className="flex items-center gap-3 pt-4 border-t">
                      {imgSrc ? (
                        <img src={imgSrc} alt={t.author_name}
                          className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/20 flex-shrink-0" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-primary font-bold text-lg flex-shrink-0 ring-2 ring-primary/20">
                          {t.author_name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{t.author_name}</p>
                        {t.author_role && (
                          <p className="text-xs text-muted-foreground truncate">{t.author_role}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Besoin d'Aide ?</h2>
            <p className="text-lg text-muted-foreground">
              Notre équipe est disponible pour répondre à toutes vos questions sur la préparation au TCF Canada.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href={`mailto:${siteSettings.email || 'contact@tcfcanada.com'}`}
              className="bg-card p-8 rounded-xl border shadow-sm hover:shadow-md transition-all group"
            >
              <Mail className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Email</h3>
              <p className="text-muted-foreground group-hover:text-primary transition-colors">
                {siteSettings.email || 'contact@tcfcanada.com'}
              </p>
            </a>
            <a
              href={`https://wa.me/${(siteSettings.whatsapp || '+23057235683').replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-card p-8 rounded-xl border shadow-sm hover:shadow-md transition-all group"
            >
              <MessageCircle className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">WhatsApp</h3>
              <p className="text-muted-foreground group-hover:text-primary transition-colors">
                {siteSettings.phone || '+23057235683'}
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                {siteSettings.logo ? (
                  <img 
                    src={siteSettings.logo.startsWith('http') ? siteSettings.logo : `${process.env.REACT_APP_BACKEND_URL || ''}${siteSettings.logo}`} 
                    alt={siteSettings.site_name || 'Logo'} 
                    className="h-14 w-14 object-contain rounded-xl shadow-md"
                  />
                ) : (
                  <div className="h-14 w-14 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-2xl font-bold text-primary-foreground">
                      {(siteSettings.site_name || 'TCF').charAt(0)}
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-primary">{siteSettings.site_name || 'TCF Canada'}</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Plateforme spécialisée dans la préparation au TCF Canada. Tests en conditions réelles.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Liens rapides</h4>
              <div className="space-y-2 text-sm">
                <a href="#epreuves" className="block text-muted-foreground hover:text-primary transition-colors">Les 4 Épreuves</a>
                <Link to={invitationCode ? `/methodologie?code=${invitationCode}` : '/methodologie'} className="block text-muted-foreground hover:text-primary transition-colors">Méthodologie</Link>
                <Link to={invitationCode ? `/tarification?code=${invitationCode}` : '/tarification'} className="block text-muted-foreground hover:text-primary transition-colors">Tarification</Link>
                <Link to={getLoginUrl()} className="block text-muted-foreground hover:text-primary transition-colors">Se connecter</Link>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="font-semibold mb-4">Suivez-nous</h4>
              <div className="flex space-x-4">
                {siteSettings.facebook && (
                  <a 
                    href={siteSettings.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-secondary hover:bg-blue-600 hover:text-white transition-all"
                    data-testid="social-facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {siteSettings.youtube && (
                  <a 
                    href={siteSettings.youtube} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-secondary hover:bg-red-600 hover:text-white transition-all"
                    data-testid="social-youtube"
                  >
                    <Youtube className="h-5 w-5" />
                  </a>
                )}
                {siteSettings.instagram && (
                  <a 
                    href={siteSettings.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-secondary hover:bg-pink-600 hover:text-white transition-all"
                    data-testid="social-instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {siteSettings.telegram && (
                  <a 
                    href={siteSettings.telegram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-secondary hover:bg-blue-500 hover:text-white transition-all"
                    data-testid="social-telegram"
                  >
                    <Send className="h-5 w-5" />
                  </a>
                )}
                <a 
                  href={`https://wa.me/${(siteSettings.whatsapp || '+23057235683').replace(/[^0-9]/g, '')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-secondary hover:bg-green-600 hover:text-white transition-all"
                  data-testid="social-whatsapp"
                >
                  <MessageCircle className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t pt-8 text-center text-sm text-muted-foreground">
            <p>© 2026 {siteSettings.site_name || 'TCF Canada'} - Tous droits réservés</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
